/**
 * The arithmetic behind the zone maps: rings, extents, and snapping.
 *
 * Pure on purpose. Everything here is a few lines of geometry that is easy to get subtly
 * wrong — a circle that comes out as an ellipse, a snap that grabs a corner on the far side
 * of the city because the tolerance was in the wrong unit — and a function that takes
 * numbers and returns numbers can be tested in milliseconds without a map or a browser.
 * `ZoneMap` and `ZonesOverview` do the drawing; this file decides what to draw.
 */

import type { Zone, ZoneVertex } from '../api';

/** Two corners in the order MapLibre's `fitBounds` wants them: south-west, then north-east. */
export type Extent = [[number, number], [number, number]];

/** A point on the screen, in the map's own pixel space. */
export interface Pixel {
  x: number;
  y: number;
}

/** Enough of a zone to draw it: the identity, and the ring that outlines it. */
export interface ZoneOutline {
  id: string;
  displayName: string;
  kind: Zone['kind'];
  isActive: boolean;
  /** How it is stored: a circle's ring is generated for drawing, and has no corners of its own. */
  shape: Zone['shape'];
  /** Which of the zone colours it is drawn in: an index into `--zone-1` … `--zone-N`. */
  hue: number;
  /** 1 at the root. A zone inside another is filled a little more densely, to read over it. */
  depth: number;
  /** The outline, open — the last corner is not a repeat of the first. */
  ring: ZoneVertex[];
}

/** How many zone colours the stylesheet defines. */
export const ZONE_HUES = 8;

/**
 * The order colours are handed out in, so that the first few — all most firms ever use — are
 * as far apart as the palette allows: blue, orange, green, purple, yellow, cyan, pink, red.
 * Taken in index order, the third zone got purple beside the fourth's pink and the fifth's
 * cyan beside the first's blue, which on a map is three shades of two colours (#373).
 */
const HUE_PREFERENCE = [0, 1, 3, 2, 6, 5, 4, 7] as const;

/** How far apart two zones' outlines may be and still count as beside each other: about 150 m. */
const NEIGHBOUR_DEGREES = 0.0015;

/** What `huesFor` needs of a zone: its place in the tree, the colour chosen for it, its outline. */
export interface HuedZone {
  id: string;
  parentId: string | null;
  /** The colour the office chose and stored, or null to have one chosen. */
  hue?: number | null;
  /** The outline, open. Two zones whose outlines come close are kept apart in colour too. */
  ring?: readonly ZoneVertex[];
}

/**
 * A colour for every zone: the one the office chose, or one chosen so that no zone shares a
 * colour with anything it is inside, beside it in the tree, or touching it on the map.
 *
 * Those are the pairs that matter: the question the map exists to answer — do these two
 * meet, or is there a street between them? — cannot be answered when both are the same blue.
 * The tree alone is not enough. Two districts under different parents can share a border, and
 * BulgariaTaxi's Добротица and Гробища, side by side on the map, came out the same blue (#373);
 * so outlines that come within `NEIGHBOUR_DEGREES` of each other count as neighbours as well.
 *
 * A stored colour is placed first and never moved; the rest are chosen greedily, in list
 * order, taking the first colour in `HUE_PREFERENCE` that none of a zone's kin or neighbours
 * already has. When every colour is taken the least used among them is repeated — never an
 * ancestor's, which would hide the zone inside it.
 */
export function huesFor(zones: readonly HuedZone[], hues = ZONE_HUES): Map<string, number> {
  const chosen = new Map<string, number>();
  const parentOf = new Map(zones.map((zone) => [zone.id, zone.parentId]));
  const boxes = new Map(zones.map((zone) => [zone.id, extentOf(zone.ring ? [[...zone.ring]] : [])]));
  const order = HUE_PREFERENCE.filter((hue) => hue < hues);

  for (const zone of zones) {
    if (typeof zone.hue === 'number' && zone.hue >= 0 && zone.hue < hues) {
      chosen.set(zone.id, zone.hue);
    }
  }

  for (const zone of zones) {
    if (chosen.has(zone.id)) continue;

    const ancestors = new Set<number>();

    for (let above = zone.parentId; above !== null && above !== undefined; ) {
      const ancestor = chosen.get(above);

      if (ancestor !== undefined) ancestors.add(ancestor);

      above = parentOf.get(above) ?? null;
    }

    // Everything else this zone must not match, counted, so that a full palette repeats the
    // colour it would cost least to repeat.
    const others = new Map<number, number>();
    const box = boxes.get(zone.id) ?? null;

    for (const other of zones) {
      const hue = chosen.get(other.id);

      if (other.id === zone.id || hue === undefined) continue;

      const sibling = other.parentId === zone.parentId;
      const touching = box !== null && near(box, boxes.get(other.id) ?? null);

      if (sibling || touching) others.set(hue, (others.get(hue) ?? 0) + 1);
    }

    const free = order.find((hue) => !ancestors.has(hue) && !others.has(hue));
    const fallback = order
      .filter((hue) => !ancestors.has(hue))
      .sort((a, b) => (others.get(a) ?? 0) - (others.get(b) ?? 0))[0];

    chosen.set(zone.id, free ?? fallback ?? order[0] ?? 0);
  }

  return chosen;
}

/** Whether two boxes overlap or come within `NEIGHBOUR_DEGREES` of each other. */
function near(a: Extent, b: Extent | null): boolean {
  if (b === null) return false;

  const [[westA, southA], [eastA, northA]] = a;
  const [[westB, southB], [eastB, northB]] = b;

  return (
    westA - NEIGHBOUR_DEGREES <= eastB &&
    westB - NEIGHBOUR_DEGREES <= eastA &&
    southA - NEIGHBOUR_DEGREES <= northB &&
    southB - NEIGHBOUR_DEGREES <= northA
  );
}

/** How close, in screen pixels, a click has to land to a neighbour's edge to snap onto it. */
export const SNAP_PIXELS = 12;

/**
 * A circle as a ring of points, for drawing only.
 *
 * The stored shape is still a centre and a radius; this is what a circle looks like once
 * somebody has to see it. Latitude is corrected for the longitude spacing, or a circle
 * drawn in Sofia would come out as an ellipse.
 */
export function circleRing(centre: ZoneVertex, radiusMetres: number, points = 64): ZoneVertex[] {
  const metresPerDegree = 111_320;
  const latitudeSpread = radiusMetres / metresPerDegree;
  const longitudeSpread =
    radiusMetres / (metresPerDegree * Math.cos((centre.latitude * Math.PI) / 180));

  return Array.from({ length: points }, (_, index) => {
    const angle = (index / points) * 2 * Math.PI;

    return {
      longitude: centre.longitude + longitudeSpread * Math.cos(angle),
      latitude: centre.latitude + latitudeSpread * Math.sin(angle),
    };
  });
}

/**
 * A ring with its closing point removed.
 *
 * The API stores a polygon closed — the first corner repeated at the end, which is what
 * the geometry library wants — and hands it back that way. The editor counts corners and
 * lets a person undo the last one, and a closing point that is not really a corner would
 * make "18 corners" read as 19 and "undo" silently remove nothing visible.
 */
export function openRing(ring: ZoneVertex[]): ZoneVertex[] {
  if (ring.length < 2) return ring;

  const first = ring[0];
  const last = ring[ring.length - 1];

  return first.longitude === last.longitude && first.latitude === last.latitude
    ? ring.slice(0, -1)
    : ring;
}

/** The ring that outlines a zone, whichever shape it is stored as. Empty when it has none yet. */
export function ringOf(zone: {
  shape: Zone['shape'];
  centreLongitude: number | null;
  centreLatitude: number | null;
  radiusMetres: number | null;
  boundary: ZoneVertex[] | null;
}): ZoneVertex[] {
  if (zone.shape === 'Circle') {
    return zone.centreLongitude === null || zone.centreLatitude === null
      ? []
      : circleRing(
          { longitude: zone.centreLongitude, latitude: zone.centreLatitude },
          zone.radiusMetres ?? 0,
        );
  }

  return openRing(zone.boundary ?? []);
}

/** What the maps need of a zone, in the colour `huesFor` chose for it. */
export function outlineOf(zone: Zone, hue: number): ZoneOutline {
  return {
    id: zone.id,
    displayName: zone.displayName,
    kind: zone.kind,
    isActive: zone.isActive,
    shape: zone.shape,
    hue,
    depth: zone.depth,
    ring: ringOf(zone),
  };
}

/**
 * The box around every point given, or null when there are none.
 *
 * Built from minima and maxima rather than from any two points, for the reason
 * `boundsFor` in the map module spells out: `fitBounds` reads an inverted longitude as a
 * box that crosses the antimeridian and fits the whole planet.
 */
export function extentOf(rings: ZoneVertex[][]): Extent | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  for (const ring of rings) {
    for (const point of ring) {
      west = Math.min(west, point.longitude);
      east = Math.max(east, point.longitude);
      south = Math.min(south, point.latitude);
      north = Math.max(north, point.latitude);
    }
  }

  return west === Infinity ? null : [[west, south], [east, north]];
}

/**
 * Where a zone's name goes: the mean of its corners.
 *
 * Not the true centroid, and not meant to be. Zones are drawn convex-ish by hand around a
 * district or a forecourt, and for those the mean of the corners lands inside every time;
 * the exact area-weighted centroid is more arithmetic for a label nobody would see move.
 */
export function labelPointOf(ring: ZoneVertex[]): ZoneVertex | null {
  if (ring.length === 0) return null;

  let longitude = 0;
  let latitude = 0;

  for (const point of ring) {
    longitude += point.longitude;
    latitude += point.latitude;
  }

  return { longitude: longitude / ring.length, latitude: latitude / ring.length };
}

/**
 * The point a click really meant, when it was meant to touch a neighbour.
 *
 * Two zones that are supposed to meet — the centre and the district beside it — have to
 * share their boundary exactly, or there is a sliver of street between them that belongs to
 * neither and a pickup there falls through to the city. Nobody can click the same
 * coordinate twice by hand, so a click within `tolerance` pixels of a neighbour's corner
 * lands on that corner, and one within the same distance of a neighbour's edge lands on
 * the nearest point along it. A corner wins over an edge, because the corner is where the
 * edges of two neighbours meet and is the point a third zone almost always wants.
 *
 * The tolerance is in screen pixels and the rings are in degrees, so the caller hands in
 * the map's own projection. Measured on screen rather than on the ground because that is
 * where the mistake happens: the same ten metres is a hair at city zoom and half the map
 * on a forecourt, and "close" means close to what the person is looking at.
 */
export function snapTo(
  point: ZoneVertex,
  rings: ZoneVertex[][],
  project: (vertex: ZoneVertex) => Pixel,
  tolerance = SNAP_PIXELS,
): ZoneVertex {
  const target = project(point);
  const limit = tolerance * tolerance;

  let nearestCorner: { vertex: ZoneVertex; distance: number } | null = null;
  let nearestEdge: { vertex: ZoneVertex; distance: number } | null = null;

  for (const ring of rings) {
    if (ring.length === 0) continue;

    const projected = ring.map(project);

    for (let index = 0; index < ring.length; index++) {
      const corner = projected[index];
      const cornerDistance = squared(target, corner);

      if (cornerDistance <= limit && (nearestCorner === null || cornerDistance < nearestCorner.distance)) {
        nearestCorner = { vertex: ring[index], distance: cornerDistance };
      }

      // Every ring is closed for this purpose, so the last corner has an edge back to
      // the first — a circle or a district has no open side.
      if (ring.length < 2) continue;

      const next = (index + 1) % ring.length;
      const along = alongSegment(target, corner, projected[next]);

      if (along === null) continue;

      const edgePoint = {
        x: corner.x + (projected[next].x - corner.x) * along,
        y: corner.y + (projected[next].y - corner.y) * along,
      };
      const edgeDistance = squared(target, edgePoint);

      if (edgeDistance <= limit && (nearestEdge === null || edgeDistance < nearestEdge.distance)) {
        nearestEdge = {
          vertex: {
            longitude: ring[index].longitude + (ring[next].longitude - ring[index].longitude) * along,
            latitude: ring[index].latitude + (ring[next].latitude - ring[index].latitude) * along,
          },
          distance: edgeDistance,
        };
      }
    }
  }

  return nearestCorner?.vertex ?? nearestEdge?.vertex ?? point;
}

/**
 * Where a new corner goes once the shape exists: into the side nearest the click.
 *
 * Appending every click to the end of the list is right while a boundary is being drawn
 * and wrong once it exists. A click a hair outside the tolerance of a side was appended,
 * and the closing lines then ran from the last corner across the whole shape — a mess
 * that was saved before it was noticed. So from three corners on, no click appends: the
 * nearest side takes it, where every polygon editor puts it. On the side, within
 * `tolerance` pixels, the corner lands exactly on the line, so the shape does not change
 * until it is dragged; beside the side, it lands where clicked and the side bends out to
 * it. The closing side counts, and inserting into it is appending — which is what the
 * fourth corner of a shape drawn round in order needs. Null under three corners, when
 * there is no side to speak of.
 */
export function insertionOn(
  point: ZoneVertex,
  ring: ZoneVertex[],
  project: (vertex: ZoneVertex) => Pixel,
  tolerance = SNAP_PIXELS,
): { at: number; vertex: ZoneVertex; onSide: boolean } | null {
  if (ring.length < 3) return null;

  const target = project(point);
  const projected = ring.map(project);

  let nearest: { at: number; vertex: ZoneVertex; distance: number } | null = null;

  for (let index = 0; index < ring.length; index++) {
    const next = (index + 1) % ring.length;
    // Clamped to the side, so a click beyond either end is measured to that end rather
    // than skipped: every click has a nearest side.
    const along = Math.max(0, Math.min(1, alongSegment(target, projected[index], projected[next]) ?? 0));

    const distance = squared(target, {
      x: projected[index].x + (projected[next].x - projected[index].x) * along,
      y: projected[index].y + (projected[next].y - projected[index].y) * along,
    });

    if (nearest === null || distance < nearest.distance) {
      nearest = {
        at: index + 1,
        vertex: {
          longitude: ring[index].longitude + (ring[next].longitude - ring[index].longitude) * along,
          latitude: ring[index].latitude + (ring[next].latitude - ring[index].latitude) * along,
        },
        distance,
      };
    }
  }

  if (nearest === null) return null;

  const onSide = nearest.distance <= tolerance * tolerance;

  return { at: nearest.at, vertex: onSide ? nearest.vertex : point, onSide };
}

function squared(a: Pixel, b: Pixel): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;

  return dx * dx + dy * dy;
}

/**
 * How far along the segment from `a` to `b` the foot of the perpendicular from `p`
 * falls, as a fraction, or null when it falls beyond either end — those cases belong to
 * the corners, which `snapTo` checks separately and `insertionOn` clamps to.
 */
function alongSegment(p: Pixel, a: Pixel, b: Pixel): number | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = dx * dx + dy * dy;

  if (length === 0) return null;

  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / length;

  return t <= 0 || t >= 1 ? null : t;
}

/** The smallest and largest circle the API takes, in metres. */
export const RADIUS_MIN = 20;
export const RADIUS_MAX = 100_000;

/**
 * The radius slider's scale: fine where towns are measured, coarse where only a region is.
 *
 * A straight 20 m–100 km bar puts 500 m at the far left, where a pixel is two hundred metres
 * and nobody can set a rank. So the bar is three straight runs end to end: 10 m steps to 2 km,
 * 100 m steps to 20 km, and 1 km steps to 100 km. Each step is one position, so the arrow
 * keys move by a sensible amount at every size (#372).
 */
const RADIUS_RUNS = [
  { from: RADIUS_MIN, to: 2_000, step: 10 },
  { from: 2_000, to: 20_000, step: 100 },
  { from: 20_000, to: RADIUS_MAX, step: 1_000 },
] as const;

/** How many positions the slider has: the last one is `RADIUS_MAX`. */
export const RADIUS_POSITIONS = RADIUS_RUNS.reduce((sum, run) => sum + (run.to - run.from) / run.step, 0);

/** The radius at a slider position. */
export function radiusAt(position: number): number {
  let left = Math.max(0, Math.round(position));

  for (const run of RADIUS_RUNS) {
    const steps = (run.to - run.from) / run.step;

    if (left <= steps) return run.from + left * run.step;

    left -= steps;
  }

  return RADIUS_MAX;
}

/** The slider position nearest a radius — a typed 507 m sits between two steps, and that is fine. */
export function positionOf(radius: number): number {
  const metres = Math.min(RADIUS_MAX, Math.max(RADIUS_MIN, Number.isFinite(radius) ? radius : RADIUS_MIN));
  let before = 0;

  for (const run of RADIUS_RUNS) {
    const steps = (run.to - run.from) / run.step;

    if (metres <= run.to) return before + Math.round((metres - run.from) / run.step);

    before += steps;
  }

  return RADIUS_POSITIONS;
}
